import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface StatCardProps {
    title: string;
    value: React.ReactNode;
    description: string;
    subtitle?: React.ReactNode;
    className?: string;
}

export function StatCard({ title, value, description, subtitle, className }: StatCardProps) {
    return (
        <Card className={cn("border-2 border-border rounded-2xl", className)}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground transition-colors hover:text-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="max-w-xs text-xs">{description}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </CardHeader>
            <CardContent>
                <div className="text-xl sm:text-2xl font-bold break-words">{value}</div>
                {subtitle && (
                    <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
                )}
            </CardContent>
        </Card>
    );
}

// Currency display with smaller symbol - consistent styling across app
export const CurrencyDisplay = ({ amount, className = '' }: { amount: number; className?: string }) => {
    const absAmount = Math.abs(amount);
    const formattedValue = new Intl.NumberFormat('en-GH', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(absAmount);

    return (
        <span className={className}>
            {formattedValue}
            <span className="text-sm font-normal">GH₵</span>
        </span>
    );
};

// Percent display with smaller symbol - consistent styling across app
export const PercentDisplay = ({ value }: { value: number }) => (
    <>
        {value.toFixed(1)}
        <span className="text-[0.6em] font-normal">%</span>
    </>
);
